import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo } from 'react';
import { ProductionConfig, ProductionOutput } from '@/types/production.types';
import { InstrumentForgeBlendConfig, BlendedProductionOutput } from '@features/InstrumentForge/types/instrumentForge.types';
import { generateProductionOutput } from '@utils/productionEngine';
import { generateBlendedOutput as generateInstrumentForgeOutput } from '@features/InstrumentForge/utils/profileBlender';
import { formatInstrumentForgePhysics } from '@/shared/instrumentForgePhysicsText';
import { useUndoStack } from '@hooks/useUndoStack';

interface ProductionUndoState {
  config: ProductionConfig;
  instrumentForgeConfig: InstrumentForgeBlendConfig | null;
}

interface ProductionConfigContextType {
  // Existing Production Intelligence
  config: ProductionConfig;
  output: ProductionOutput | null;
  applyProductionProfile: (config: ProductionConfig) => void;
  clearProductionProfile: () => void;
  getProductionArchitecture: () => string;
  isProductionProfileActive: boolean;

  // NEW: InstrumentForge Integration
  instrumentForgeConfig: InstrumentForgeBlendConfig | null;
  instrumentForgeOutput: BlendedProductionOutput | null;
  applyInstrumentForge: (config: InstrumentForgeBlendConfig) => void;
  clearInstrumentForge: () => void;
  getInstrumentForgePhysics: () => string;
  isInstrumentForgeActive: boolean;

  // NEW: Combined output for prompt builder
  getFullProductionArchitecture: () => string;

  // Undo/Redo
  undoProduction: () => void;
  redoProduction: () => void;
  canUndoProduction: boolean;
  canRedoProduction: boolean;
}

const defaultConfig: ProductionConfig = {
  keyTempoMode: 'auto',
  selectedKey: null,
  selectedTempo: 100,
  tempoProfile: null,
  instrumentStack: { core: [], color: [], texture: [], optional: [] },
  customInstruments: [],
  mixTemplate: null,
  customPositions: [],
  humanization: 0,
  suggestion: null
};

const ProductionConfigContext = createContext<ProductionConfigContextType | null>(null);

export function ProductionConfigProvider({ children }: { children?: ReactNode }) {
  // Existing Production Intelligence state
  const [config, setConfig] = useState<ProductionConfig>(defaultConfig);
  const [output, setOutput] = useState<ProductionOutput | null>(null);

  // NEW: InstrumentForge state
  const [instrumentForgeConfig, setInstrumentForgeConfig] = useState<InstrumentForgeBlendConfig | null>(null);

  // Undo/Redo
  const productionUndo = useUndoStack<ProductionUndoState>({ maxHistory: 30 });

  useEffect(() => {
    productionUndo.pushState({ config, instrumentForgeConfig });
  }, [config, instrumentForgeConfig]);

  const undoProduction = useCallback(() => {
    const state = productionUndo.undo();
    if (state) {
      setConfig(state.config);
      const newOutput = generateProductionOutput(state.config);
      setOutput(newOutput);
      setInstrumentForgeConfig(state.instrumentForgeConfig);
    }
  }, [productionUndo]);

  const redoProduction = useCallback(() => {
    const state = productionUndo.redo();
    if (state) {
      setConfig(state.config);
      const newOutput = generateProductionOutput(state.config);
      setOutput(newOutput);
      setInstrumentForgeConfig(state.instrumentForgeConfig);
    }
  }, [productionUndo]);

  // Reactive InstrumentForge output
  const instrumentForgeOutput = useMemo<BlendedProductionOutput | null>(() => {
    if (!instrumentForgeConfig || !instrumentForgeConfig.profileAKey) return null;
    return generateInstrumentForgeOutput(instrumentForgeConfig);
  }, [instrumentForgeConfig]);

  // Existing handlers
  const applyProductionProfile = useCallback((newConfig: ProductionConfig) => {
    setConfig(newConfig);
    const newOutput = generateProductionOutput(newConfig);
    setOutput(newOutput);
  }, []);

  const clearProductionProfile = useCallback(() => {
    setConfig(defaultConfig);
    setOutput(null);
  }, []);

  const getProductionArchitecture = useCallback((): string => {
    if (!output) return '';
    return output.fullPromptBlock;
  }, [output]);

  // NEW: InstrumentForge handlers
  const applyInstrumentForge = useCallback((ifConfig: InstrumentForgeBlendConfig) => {
    setInstrumentForgeConfig(ifConfig);
  }, []);

  const clearInstrumentForge = useCallback(() => {
    setInstrumentForgeConfig(null);
  }, []);

  const getInstrumentForgePhysics = useCallback((): string => {
    if (!instrumentForgeOutput) return '';
    return formatInstrumentForgePhysics(instrumentForgeOutput.physicsLayers);
  }, [instrumentForgeOutput]);

  // NEW: Combined output for prompt builder
  const getFullProductionArchitecture = useCallback((): string => {
    const parts: string[] = [];
    
    // Existing production architecture (key, tempo, instrument stack, mix hints)
    const prodArch = getProductionArchitecture();
    if (prodArch) {
      parts.push(prodArch);
    }
    
    // InstrumentForge physics layers
    const ifPhysics = getInstrumentForgePhysics();
    if (ifPhysics) {
      parts.push('');
      parts.push('[INSTRUMENTFORGE PHYSICS LAYERS]');
      parts.push(ifPhysics);
      parts.push('[END INSTRUMENTFORGE]');
    }
    
    return parts.join('\n');
  }, [getProductionArchitecture, getInstrumentForgePhysics]);

  const isProductionProfileActive = output !== null && 
    (config.selectedKey !== null || config.instrumentStack.core.length > 0);
  
  const isInstrumentForgeActive = instrumentForgeOutput !== null;

  return (
    <ProductionConfigContext.Provider value={{
      config,
      output,
      applyProductionProfile,
      clearProductionProfile,
      getProductionArchitecture,
      isProductionProfileActive,
      
      // InstrumentForge
      instrumentForgeConfig,
      instrumentForgeOutput,
      applyInstrumentForge,
      clearInstrumentForge,
      getInstrumentForgePhysics,
      isInstrumentForgeActive,
      
      // Combined
      getFullProductionArchitecture,

      // Undo/Redo
      undoProduction,
      redoProduction,
      canUndoProduction: productionUndo.canUndo,
      canRedoProduction: productionUndo.canRedo,
    }}>
      {children}
    </ProductionConfigContext.Provider>
  );
}

export function useProductionConfig() {
  const context = useContext(ProductionConfigContext);
  if (!context) {
    throw new Error('useProductionConfig must be used within ProductionConfigProvider');
  }
  return context;
}